import { type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiService } from 'api';
import { SET_CURRENCY } from 'common/actions/format/types';
import { useStore } from 'common/store';
import { AdditionalCurrency, Currency, CurrencyCode, DispatchFormat, Format, Units } from 'common/types/format';
import { UseStoreValues } from 'common/types/store';
import { MILLIONS, THOUSANDS } from 'components/FeaturedSpreadsheet/constants';
import { useCurrencies } from 'components/Grid/GridFormatter/useCurrencies';
import { worldCurrencies } from 'utilities';
import { UseWaterfallFXSyncProps } from './UseWaterfallFXSync.props';
import { getEffectiveWaterfallExchangeRate, getWaterfallSourceCurrency } from './useWaterfallFXSync.utils';
import { useCreateExchangeRateSheet } from '../ExchangeRateSheet/useCreateExchangeRateSheet';
import { WaterfallFXContextType } from '../WaterfallFXContext/WaterfallFXContext';

const getCurrencySymbol = (currency?: string) =>
  currency ? ((worldCurrencies as Record<string, string>)[currency] ?? '') : '';

export const useWaterfallFXSync = ({
  companyId,
  page,
  units,
  measurementDate,
  marketDate,
  selectedCurrency,
  exchangeRateFromUrl,
  isExchangeRateOverridden,
  onCurrencyChange,
  onUnitsChange,
  onExchangeRateResolved,
}: UseWaterfallFXSyncProps) => {
  const [{ companyInfo }] = useStore() as UseStoreValues;
  const sourceCurrency = getWaterfallSourceCurrency(companyInfo, companyId, page);
  const [currencyMenuAnchorEl, setCurrencyMenuAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isExchangeRateRetrying, setIsExchangeRateRetrying] = useState(false);
  const { currencies, usedCurrencies, showMore, showLess } = useCurrencies();
  const { exchangeRateSheet } = useCreateExchangeRateSheet();

  const shouldFetchExchangeRate = Boolean(
    sourceCurrency &&
      sourceCurrency !== selectedCurrency &&
      measurementDate?.id &&
      marketDate &&
      !isExchangeRateOverridden &&
      exchangeRateFromUrl == null
  );

  const {
    data: fetchedExchangeRate,
    isError: isExchangeRateError,
    isFetching: isExchangeRateFetching,
    refetch: refetchExchangeRateQuery,
  } = useQuery({
    queryKey: ['waterfall-exchange-rate', sourceCurrency, selectedCurrency, measurementDate?.id, marketDate],
    queryFn: async () => {
      if (!sourceCurrency || !measurementDate?.id || !marketDate) {
        throw new Error('Missing exchange rate context');
      }
      const response = await ApiService.apiValuationGetExchangeRateRead(
        String(measurementDate.id),
        `$${String(selectedCurrency)}${String(sourceCurrency)}:`,
        marketDate
      );
      const numericRate = Number(
        response?.valid_exchange_data ? response?.exchange_rate?.close_price?.value : undefined
      );
      if (!Number.isFinite(numericRate) || numericRate <= 0) {
        throw new Error('Invalid exchange rate');
      }
      return numericRate;
    },
    enabled: shouldFetchExchangeRate,
    meta: { showGlobalError: false },
    retry: 1,
  });

  const refetchExchangeRate = useCallback(async () => {
    setIsExchangeRateRetrying(true);
    try {
      return await refetchExchangeRateQuery();
    } finally {
      setIsExchangeRateRetrying(false);
    }
  }, [refetchExchangeRateQuery]);

  const effectiveExchangeRate = useMemo(
    () =>
      getEffectiveWaterfallExchangeRate({
        sourceCurrency,
        selectedCurrency,
        exchangeRateFromUrl,
        fetchedExchangeRate,
        shouldFetchExchangeRate,
      }),
    [exchangeRateFromUrl, fetchedExchangeRate, selectedCurrency, shouldFetchExchangeRate, sourceCurrency]
  );
  const isExchangeRateReady =
    Number.isFinite(effectiveExchangeRate) && effectiveExchangeRate != null && effectiveExchangeRate > 0;
  const hasExchangeRateLoadError =
    shouldFetchExchangeRate && isExchangeRateError && !isExchangeRateFetching && !isExchangeRateReady;

  // Materialize only a resolved rate in the URL; same-currency selections always resolve to one.
  useEffect(() => {
    if (sourceCurrency === selectedCurrency) {
      if (exchangeRateFromUrl !== 1) {
        onExchangeRateResolved(1);
      }
      return;
    }
    if (shouldFetchExchangeRate && fetchedExchangeRate != null && !isExchangeRateFetching) {
      onExchangeRateResolved(fetchedExchangeRate);
    }
  }, [
    exchangeRateFromUrl,
    fetchedExchangeRate,
    isExchangeRateFetching,
    onExchangeRateResolved,
    selectedCurrency,
    shouldFetchExchangeRate,
    sourceCurrency,
  ]);

  const format = useMemo<Format>(
    () => ({
      currency: {
        code: selectedCurrency,
        sourceCurrency,
        symbol: getCurrencySymbol(String(selectedCurrency)),
        units,
        exchangeRate: effectiveExchangeRate ?? undefined,
        measurementDateId: measurementDate?.id,
        marketDate: marketDate ?? undefined,
      },
    }),
    [effectiveExchangeRate, marketDate, measurementDate?.id, selectedCurrency, sourceCurrency, units]
  );

  const inputFormat = useMemo<Format>(
    () => ({
      currency: {
        ...format.currency,
        code: sourceCurrency,
        sourceCurrency,
        symbol: getCurrencySymbol(String(sourceCurrency ?? '')),
        exchangeRate: 1,
      },
    }),
    [format.currency, sourceCurrency]
  );

  const handleCurrencyMenuOpen = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => setCurrencyMenuAnchorEl(event.currentTarget),
    []
  );
  const handleCurrencyMenuClose = useCallback(() => setCurrencyMenuAnchorEl(null), []);

  const handleCurrencyChange = useCallback(
    (currency: Currency) => {
      if (currency.code && currency.code !== selectedCurrency) {
        onCurrencyChange(currency.code);
      }
      handleCurrencyMenuClose();
    },
    [handleCurrencyMenuClose, onCurrencyChange, selectedCurrency]
  );

  const handleUnitClick = useCallback(() => {
    const numberUnits: Units[] = ['' as Units, THOUSANDS as Units, MILLIONS as Units];
    const currentIndex = numberUnits.indexOf(units);
    onUnitsChange(numberUnits[(currentIndex + 1) % numberUnits.length]);
  }, [onUnitsChange, units]);

  const formatDispatch = useCallback<DispatchFormat>(
    action => {
      const currency = (action as { type?: string; currency?: Currency })?.currency;
      if ((action as { type?: string })?.type !== SET_CURRENCY || !currency) {
        return;
      }
      if (currency.code) {
        onCurrencyChange(currency.code);
      }
      if (currency.units !== undefined) {
        onUnitsChange(currency.units);
      }
    },
    [onCurrencyChange, onUnitsChange]
  );

  const selectedAdditionalCurrency = useMemo<AdditionalCurrency>(
    () => ({
      sourceCurrency: selectedCurrency,
      symbol: getCurrencySymbol(String(selectedCurrency)),
      exchangeRate: effectiveExchangeRate ?? undefined,
      currencyType: 'waterfall_foreign_currency',
    }),
    [effectiveExchangeRate, selectedCurrency]
  );

  const currencyList = useMemo<AdditionalCurrency[]>(
    () =>
      Object.keys(worldCurrencies).map(currency => ({
        sourceCurrency: currency as CurrencyCode,
        symbol: getCurrencySymbol(currency),
        currencyType: 'waterfall_foreign_currency',
      })),
    []
  );

  const currencyDisplayLabel = useMemo(() => {
    if (sourceCurrency && sourceCurrency !== selectedCurrency) {
      const rate = effectiveExchangeRate ? Math.round(effectiveExchangeRate * 100) / 100 : '?';
      return `1 ${String(sourceCurrency)} → ${rate} ${String(selectedCurrency)}`;
    }
    return String(selectedCurrency);
  }, [effectiveExchangeRate, selectedCurrency, sourceCurrency]);

  const currencyTooltipTitle = useMemo(() => {
    if (sourceCurrency && sourceCurrency !== selectedCurrency && effectiveExchangeRate) {
      return `1 ${String(sourceCurrency)} → ${effectiveExchangeRate} ${String(selectedCurrency)}`;
    }
    return undefined;
  }, [effectiveExchangeRate, selectedCurrency, sourceCurrency]);

  const unitDisplayLabel = useMemo(() => {
    const symbol = getCurrencySymbol(String(selectedCurrency)) || '$';
    if (units === THOUSANDS) {
      return `(${symbol}) Thousands`;
    }
    if (units === MILLIONS) {
      return `(${symbol}) Millions`;
    }
    return symbol;
  }, [selectedCurrency, units]);

  const waterfallFXProvider = useMemo<WaterfallFXContextType>(
    () => ({
      currencyDisplayLabel,
      selectedCurrency: selectedAdditionalCurrency,
      setSelectedCurrency: currency => onCurrencyChange(currency.sourceCurrency),
      exchangeRate: effectiveExchangeRate,
      currencyList,
      isLoading: shouldFetchExchangeRate && isExchangeRateFetching,
      onCurrencyChange: handleCurrencyChange,
      onUnitsChange: handleUnitClick,
      standardCurrency: sourceCurrency ? String(sourceCurrency) : undefined,
      unitDisplayLabel,
      units,
    }),
    [
      currencyDisplayLabel,
      currencyList,
      effectiveExchangeRate,
      handleCurrencyChange,
      handleUnitClick,
      isExchangeRateFetching,
      onCurrencyChange,
      selectedAdditionalCurrency,
      shouldFetchExchangeRate,
      sourceCurrency,
      unitDisplayLabel,
      units,
    ]
  );

  return {
    format,
    inputFormat,
    formatDispatch,
    waterfallFXProvider,
    exchangeRateSheet,
    isAdditionalCurrencySelected: Boolean(sourceCurrency && sourceCurrency !== selectedCurrency),
    isCurrencyLoading: shouldFetchExchangeRate && isExchangeRateFetching,
    isExchangeRateReady,
    hasExchangeRateLoadError,
    isExchangeRateRetrying,
    refetchExchangeRate,
    currencyMenuAnchorEl,
    handleCurrencyMenuOpen,
    handleCurrencyMenuClose,
    handleCurrencyChange,
    handleUnitClick,
    unitDisplayLabel,
    currencyDisplayLabel,
    currencyTooltipTitle,
    currencies,
    usedCurrencies,
    showMore,
    showLess,
  };
};
